import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Users, 
  Database, 
  Trash2, 
  Key, 
  Download,
  Server,
  HardDrive,
  UserCog,
  Shield,
  RefreshCw,
  Lock,
  Pencil
} from 'lucide-react';

const ADMIN_EMAIL = 'max@maxvalle.it';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [ftpConfig, setFtpConfig] = useState({
    host: '',
    port: '21',
    username: '',
    password: '',
    remote_path: '/backups'
  });
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [myPasswordDialogOpen, setMyPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editFullName, setEditFullName] = useState('');
  const [myNewPassword, setMyNewPassword] = useState('');
  const [myConfirmPassword, setMyConfirmPassword] = useState('');
  const [backupLoading, setBackupLoading] = useState(false);

  useEffect(() => {
    // Check if current user is admin
    const user = localStorage.getItem('user');
    if (user) {
      const userData = JSON.parse(user);
      if (userData.email === ADMIN_EMAIL) {
        setAuthorized(true);
        fetchData();
        loadFtpConfig();
      } else {
        toast.error('Accesso non autorizzato');
        navigate('/');
      }
    } else {
      navigate('/admin');
    }
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [usersRes, statsRes] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/stats')
      ]);
      setUsers(usersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dati admin');
    } finally {
      setLoading(false);
    }
  };

  const loadFtpConfig = async () => {
    try {
      const res = await api.get('/admin/ftp-config');
      if (res.data) {
        setFtpConfig(prev => ({ ...prev, ...res.data, password: '' }));
      }
    } catch (error) {
      console.log('Nessuna configurazione FTP salvata');
    }
  };

  const handleUpdatePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('La password deve essere almeno 6 caratteri');
      return;
    }
    try {
      await api.put(`/admin/users/${selectedUser.email}/password`, { password: newPassword });
      toast.success('Password aggiornata con successo');
      setPasswordDialogOpen(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (error) {
      toast.error('Errore nell\'aggiornamento password');
    }
  };

  const handleUpdateMyPassword = async () => {
    if (!myNewPassword || myNewPassword.length < 6) {
      toast.error('La password deve essere almeno 6 caratteri');
      return;
    }
    if (myNewPassword !== myConfirmPassword) {
      toast.error('Le password non corrispondono');
      return;
    }
    try {
      await api.put(`/admin/users/${ADMIN_EMAIL}/password`, { password: myNewPassword });
      toast.success('Password aggiornata con successo');
      setMyPasswordDialogOpen(false);
      setMyNewPassword('');
      setMyConfirmPassword('');
    } catch (error) {
      toast.error('Errore nell\'aggiornamento password');
    }
  };

  const handleUpdateUser = async () => {
    if (!editEmail || !editEmail.includes('@')) {
      toast.error('Inserisci un\'email valida');
      return;
    }
    try {
      await api.put(`/admin/users/${selectedUser.email}`, { 
        new_email: editEmail,
        full_name: editFullName
      });
      toast.success('Utente aggiornato con successo');
      setEditUserDialogOpen(false);
      setSelectedUser(null);
      setEditEmail('');
      setEditFullName('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento utente');
    }
  };

  const handleDeleteUser = async () => {
    try {
      await api.delete(`/admin/users/${selectedUser.email}`);
      toast.success('Utente eliminato con successo');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione utente');
    }
  };

  const handleBackupUser = async (userEmail) => {
    try {
      setBackupLoading(true);
      const response = await api.get(`/admin/users/${userEmail}/backup`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `backup_${userEmail}_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Backup scaricato');
    } catch (error) {
      toast.error('Errore nel backup');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleSaveFtpConfig = async () => {
    try {
      await api.post('/admin/ftp-config', ftpConfig);
      toast.success('Configurazione FTP salvata');
    } catch (error) {
      toast.error('Errore nel salvataggio configurazione FTP');
    }
  };

  const handleFullBackup = async () => {
    try {
      setBackupLoading(true);
      const response = await api.post('/admin/backup/full');
      toast.success(response.data.message || 'Backup completato');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel backup');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleDownloadFullBackup = async () => {
    try {
      setBackupLoading(true);
      const response = await api.get('/admin/backup/download', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `full_backup_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Backup scaricato');
    } catch (error) {
      toast.error('Errore nel download backup');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleSoftwareBackup = async () => {
    try {
      setBackupLoading(true);
      toast.info('Creazione backup software in corso... Potrebbe richiedere alcuni minuti.');
      const response = await api.post('/admin/backup/software');
      toast.success(response.data.message || 'Backup software completato e inviato via FTP');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel backup software');
    } finally {
      setBackupLoading(false);
    }
  };

  if (loading || !authorized) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="w-8 h-8 animate-spin text-[#D94E1F]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
            Pannello Admin
          </h2>
          <p className="text-slate-600">Gestione sistema e utenti</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMyPasswordDialogOpen(true)}
            className="text-[#D94E1F] border-[#D94E1F] hover:bg-[#D94E1F]/10"
          >
            <Lock className="w-4 h-4 mr-2" />
            Cambia Password
          </Button>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#D94E1F]" />
            <span className="text-sm text-slate-600">Area riservata</span>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.total_users}</p>
                <p className="text-xs text-slate-500">Utenti</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="bg-green-100 p-2 rounded-lg">
                <HardDrive className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.total_expenses}</p>
                <p className="text-xs text-slate-500">Spese</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="bg-purple-100 p-2 rounded-lg">
                <Database className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.total_withdrawals}</p>
                <p className="text-xs text-slate-500">Prelievi</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="bg-orange-100 p-2 rounded-lg">
                <Server className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.total_categories}</p>
                <p className="text-xs text-slate-500">Categorie</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="users" className="w-full">
        <div className="overflow-x-auto -mx-4 px-4 mb-6">
          <TabsList className="inline-flex min-w-max bg-slate-100 border border-slate-200 h-auto p-1.5 gap-1">
            <TabsTrigger 
              value="users" 
              className="px-4 py-2 text-sm font-medium whitespace-nowrap bg-white text-slate-700 data-[state=active]:!bg-[#D94E1F] data-[state=active]:!text-white rounded-md"
            >
              <UserCog className="w-4 h-4 mr-2" />
              Gestione Utenti
            </TabsTrigger>
            <TabsTrigger 
              value="backup" 
              className="px-4 py-2 text-sm font-medium whitespace-nowrap bg-white text-slate-700 data-[state=active]:!bg-[#D94E1F] data-[state=active]:!text-white rounded-md"
            >
              <Database className="w-4 h-4 mr-2" />
              Backup Database
            </TabsTrigger>
            <TabsTrigger 
              value="software" 
              className="px-4 py-2 text-sm font-medium whitespace-nowrap bg-white text-slate-700 data-[state=active]:!bg-[#D94E1F] data-[state=active]:!text-white rounded-md"
            >
              <HardDrive className="w-4 h-4 mr-2" />
              Backup Software
            </TabsTrigger>
            <TabsTrigger 
              value="ftp" 
              className="px-4 py-2 text-sm font-medium whitespace-nowrap bg-white text-slate-700 data-[state=active]:!bg-[#D94E1F] data-[state=active]:!text-white rounded-md"
            >
              <Server className="w-4 h-4 mr-2" />
              Configurazione FTP
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-900">Utenti Registrati</h3>
            </div>
            <div className="divide-y divide-slate-100">
              {users.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nessun utente registrato</p>
                </div>
              ) : (
                users.map((user) => (
                  <div key={user.email} className="p-4 hover:bg-slate-50 transition-colors">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div>
                        <p className="font-medium text-slate-900">{user.full_name || 'N/A'}</p>
                        <p className="text-sm text-slate-500">{user.email}</p>
                        {user.license_key && (
                          <p className="text-xs text-slate-400 mt-1">Licenza: {user.license_key}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedUser(user);
                            setEditEmail(user.email);
                            setEditFullName(user.full_name || '');
                            setEditUserDialogOpen(true);
                          }}
                          className="text-purple-600 border-purple-200 hover:bg-purple-50"
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          Modifica
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedUser(user);
                            setNewPassword('');
                            setPasswordDialogOpen(true);
                          }}
                          className="text-blue-600 border-blue-200 hover:bg-blue-50"
                        >
                          <Key className="w-4 h-4 mr-1" />
                          Password
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleBackupUser(user.email)}
                          disabled={backupLoading}
                          className="text-green-600 border-green-200 hover:bg-green-50"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Backup
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedUser(user);
                            setDeleteDialogOpen(true);
                          }}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </TabsContent>

        {/* Backup Tab */}
        <TabsContent value="backup" className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Backup Completo Database</h3>
            <p className="text-slate-600 mb-6">
              Esegui un backup completo di tutti i dati: utenti, spese, categorie, negozi, beneficiari, metodi di pagamento, utilizzatori e prelievi.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleDownloadFullBackup}
                disabled={backupLoading}
                className="bg-[#D94E1F] hover:bg-[#B83E15] text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                {backupLoading ? 'Backup in corso...' : 'Scarica Backup Locale'}
              </Button>
              <Button
                onClick={handleFullBackup}
                disabled={backupLoading}
                variant="outline"
                className="border-[#D94E1F] text-[#D94E1F] hover:bg-[#D94E1F]/10"
              >
                <Server className="w-4 h-4 mr-2" />
                {backupLoading ? 'Invio in corso...' : 'Invia Backup via FTP'}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Software Backup Tab */}
        <TabsContent value="software" className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Backup Completo Software</h3>
            <p className="text-slate-600 mb-6">
              Crea un archivio ZIP completo dell'intero software (codice sorgente, configurazioni, database) e invialo via FTP.
              Assicurati di aver configurato il server FTP nella sezione "Configurazione FTP".
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleSoftwareBackup}
                disabled={backupLoading}
                className="bg-[#D94E1F] hover:bg-[#B83E15] text-white"
              >
                <HardDrive className="w-4 h-4 mr-2" />
                {backupLoading ? 'Creazione backup in corso...' : 'Crea e Invia Backup Software via FTP'}
              </Button>
            </div>
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Nota:</strong> Il backup software include il database JSON, il codice sorgente e le configurazioni.
                L'operazione potrebbe richiedere alcuni minuti a seconda delle dimensioni.
              </p>
            </div>
          </div>
        </TabsContent>

        {/* FTP Config Tab */}
        <TabsContent value="ftp" className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Configurazione Server FTP</h3>
            <p className="text-slate-600 mb-6">
              Configura il server FTP per l'invio automatico dei backup.
            </p>
            <div className="space-y-4 max-w-md">
              <div>
                <Label className="text-slate-700">Host FTP *</Label>
                <Input
                  value={ftpConfig.host}
                  onChange={(e) => setFtpConfig({ ...ftpConfig, host: e.target.value })}
                  placeholder="ftp.esempio.com"
                  className="mt-1.5 h-12 rounded-xl"
                />
              </div>
              <div>
                <Label className="text-slate-700">Porta</Label>
                <Input
                  value={ftpConfig.port}
                  onChange={(e) => setFtpConfig({ ...ftpConfig, port: e.target.value })}
                  placeholder="21"
                  className="mt-1.5 h-12 rounded-xl"
                />
              </div>
              <div>
                <Label className="text-slate-700">Username *</Label>
                <Input
                  value={ftpConfig.username}
                  onChange={(e) => setFtpConfig({ ...ftpConfig, username: e.target.value })}
                  placeholder="username"
                  className="mt-1.5 h-12 rounded-xl"
                />
              </div>
              <div>
                <Label className="text-slate-700">Password *</Label>
                <Input
                  type="password"
                  value={ftpConfig.password}
                  onChange={(e) => setFtpConfig({ ...ftpConfig, password: e.target.value })}
                  placeholder="••••••••"
                  className="mt-1.5 h-12 rounded-xl"
                />
              </div>
              <div>
                <Label className="text-slate-700">Percorso Remoto</Label>
                <Input
                  value={ftpConfig.remote_path}
                  onChange={(e) => setFtpConfig({ ...ftpConfig, remote_path: e.target.value })}
                  placeholder="/backups"
                  className="mt-1.5 h-12 rounded-xl"
                />
              </div>
              <Button
                onClick={handleSaveFtpConfig}
                className="w-full h-12 bg-[#D94E1F] hover:bg-[#B83E15] text-white rounded-xl mt-4"
              >
                Salva Configurazione
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Modifica Password</DialogTitle>
            <DialogDescription className="text-slate-500">
              Inserisci la nuova password per {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-slate-700">Nuova Password</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Minimo 6 caratteri"
              className="mt-1.5 h-12 rounded-xl"
            />
          </div>
          <DialogFooter className="flex flex-col gap-2">
            <Button
              onClick={handleUpdatePassword}
              className="w-full h-12 bg-[#D94E1F] hover:bg-[#B83E15] text-white rounded-xl"
            >
              Aggiorna Password
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setPasswordDialogOpen(false);
                setNewPassword('');
              }}
              className="w-full h-12 rounded-xl"
            >
              Annulla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editUserDialogOpen} onOpenChange={setEditUserDialogOpen}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Modifica Utente</DialogTitle>
            <DialogDescription className="text-slate-500">
              Modifica i dati dell'utente
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-700">Nome Completo</Label>
              <Input
                type="text"
                value={editFullName}
                onChange={(e) => setEditFullName(e.target.value)}
                placeholder="Nome e Cognome"
                className="mt-1.5 h-12 rounded-xl"
              />
            </div>
            <div>
              <Label className="text-slate-700">Email</Label>
              <Input
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                placeholder="email@esempio.com"
                className="mt-1.5 h-12 rounded-xl"
              />
            </div>
          </div>
          <DialogFooter className="flex flex-col gap-2">
            <Button
              onClick={handleUpdateUser}
              className="w-full h-12 bg-[#D94E1F] hover:bg-[#B83E15] text-white rounded-xl"
            >
              Salva Modifiche
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setEditUserDialogOpen(false);
                setEditEmail('');
                setEditFullName('');
              }}
              className="w-full h-12 rounded-xl"
            >
              Annulla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white max-w-sm mx-4">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-900 text-xl font-bold">Elimina Utente</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600 text-base">
              Sei sicuro di voler eliminare l'utente <strong>{selectedUser?.email}</strong>? 
              Tutti i suoi dati (spese, prelievi, impostazioni) verranno eliminati permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col gap-2 sm:flex-col">
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="w-full h-12 bg-red-600 hover:bg-red-700 text-white rounded-xl text-base font-medium"
            >
              Elimina Utente
            </AlertDialogAction>
            <AlertDialogCancel className="w-full h-12 border-slate-300 text-slate-700 rounded-xl text-base font-medium mt-0">
              Annulla
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* My Password Dialog */}
      <Dialog open={myPasswordDialogOpen} onOpenChange={setMyPasswordDialogOpen}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Cambia la tua Password</DialogTitle>
            <DialogDescription className="text-slate-500">
              Inserisci la nuova password per il tuo account admin
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-700">Nuova Password</Label>
              <Input
                type="password"
                value={myNewPassword}
                onChange={(e) => setMyNewPassword(e.target.value)}
                placeholder="Minimo 6 caratteri"
                className="mt-1.5 h-12 rounded-xl"
              />
            </div>
            <div>
              <Label className="text-slate-700">Conferma Password</Label>
              <Input
                type="password"
                value={myConfirmPassword}
                onChange={(e) => setMyConfirmPassword(e.target.value)}
                placeholder="Ripeti la password"
                className="mt-1.5 h-12 rounded-xl"
              />
            </div>
          </div>
          <DialogFooter className="flex flex-col gap-2">
            <Button
              onClick={handleUpdateMyPassword}
              className="w-full h-12 bg-[#D94E1F] hover:bg-[#B83E15] text-white rounded-xl"
            >
              Aggiorna Password
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setMyPasswordDialogOpen(false);
                setMyNewPassword('');
                setMyConfirmPassword('');
              }}
              className="w-full h-12 rounded-xl"
            >
              Annulla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
